// Pure helpers for rollback evaluation profiles (getRollbackProfiles).

import {
  ProfileBaseline,
  ProfilePercentiles,
  ProfileRuleCondition,
  ProfileScope,
  RollbackProfileEntry,
} from '../../types/DeploymentRecord';
import { sortEnvNames } from './grouping';

export const rollbackProfilesKey = (app: string, jobId: string): string => `${app}/${jobId}`;

export const profileVersionKey = (envRegion: string, version: number): string =>
  `${envRegion}@${version}`;

/** "prod#us-west-2" → { env: "prod", region: "us-west-2" }. */
export const splitEnvRegion = (envRegion: string): { env: string; region: string } => {
  const i = envRegion.indexOf('#');
  return i < 0
    ? { env: envRegion, region: '' }
    : { env: envRegion.slice(0, i), region: envRegion.slice(i + 1) };
};

export const envRegionLabel = (envRegion: string): string => {
  const { env, region } = splitEnvRegion(envRegion);
  return region ? `${env} · ${region}` : env;
};

/** Env order from the pipeline ladder (sortEnvNames), then region. */
export const sortByEnvRegion = <T extends { env_region: string }>(entries: T[]): T[] => {
  const order = sortEnvNames(Array.from(new Set(entries.map(e => splitEnvRegion(e.env_region).env))));
  return [...entries].sort((a, b) => {
    const ea = splitEnvRegion(a.env_region);
    const eb = splitEnvRegion(b.env_region);
    return order.indexOf(ea.env) - order.indexOf(eb.env) || ea.region.localeCompare(eb.region);
  });
};

export const sortProfileEntries = (entries: RollbackProfileEntry[]): RollbackProfileEntry[] =>
  sortByEnvRegion(entries);

export const latestVersion = (entry: RollbackProfileEntry): number =>
  entry.versions.length > 0 ? Math.max(...entry.versions) : entry.pointer.version;

/** The pointer was moved back below the newest version written. */
export const isRolledBack = (entry: RollbackProfileEntry): boolean =>
  entry.pointer.version < latestVersion(entry);

const CMP_LABELS: Record<string, string> = { '>=': '≥', '<': '<' };

export const formatNumber = (n: number | undefined): string => {
  if (n === undefined || n === null || Number.isNaN(n)) return '—';
  if (Number.isInteger(n)) return n.toLocaleString('en-US');
  return String(Math.round(n * 1000) / 1000);
};

/** A rule as a sentence about the signal's value. */
export const formatRule = (rule: ProfileRuleCondition | undefined): string => {
  if (rule?.absolute) {
    const { cmp, bound } = rule.absolute;
    return `${CMP_LABELS[cmp] || cmp} ${formatNumber(bound)}`;
  }
  if (rule?.relative) {
    const { k, of, min_increase } = rule.relative;
    return `≥ ${formatNumber(k)}× ${of || 'baseline'} and ≥ +${formatNumber(min_increase)} over it`;
  }
  return '—';
};

export interface SignalRow extends ProfilePercentiles {
  signal: string;
}

export const baselineSignalRows = (baseline: ProfileBaseline | undefined): SignalRow[] =>
  Object.entries(baseline?.signals || {})
    .map(([signal, p]) => ({ signal, ...p }))
    .sort((a, b) => a.signal.localeCompare(b.signal));

const SCOPE_LABELS: Array<[keyof ProfileScope, string]> = [
  ['function_name', 'Function'],
  ['service', 'Service'],
  ['cluster', 'Cluster'],
  ['target_groups', 'Target groups'],
  ['api_name', 'API'],
  ['env', 'Env'],
  ['account', 'Account'],
];

export const scopeFields = (scope: ProfileScope | undefined): Array<{ label: string; value: string }> =>
  SCOPE_LABELS.flatMap(([key, label]) => {
    const v = scope?.[key];
    const value = Array.isArray(v) ? v.join(', ') : v;
    return value ? [{ label, value }] : [];
  });
